import { useDynamicContentForHome } from '@grupoboticario/barril-del-chavo'
import { UseQueryResult } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useProductsQueries, UseProductsQueriesParams } from '../useProducts'
import { useResellerContext } from '../useResellerContext'
import { getShowcasesFromDynamicContent, ProductEnhancedDynamicShowcase } from './utils'

class UseDynamicShowcasesError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'UseDynamicShowcasesError'
  }
}

export type UseDynamicShowcasesParams = {
  /**
   * Nome do componente de vitrines dinâmicas no conteúdo dinâmico. Chave `component` na estrutura do documento Prismic.
   */
  showcasesComponentName: string
  maxItemsPerShowcase: number
}

/**
 * Hook para obter as vitrines de foco dinâmicas da home, a partir do conteúdo dinâmico.
 */
export function useDynamicShowcases({
  showcasesComponentName,
  maxItemsPerShowcase,
}: UseDynamicShowcasesParams) {
  const resellerContext = useResellerContext()
  const dynamicContentForHome = useDynamicContentForHome({
    resellerSlug: resellerContext!.reseller!.catalogSlug,
  })

  const dynamicShowcases = useMemo(() => {
    try {
      return getShowcasesFromDynamicContent(dynamicContentForHome.data, showcasesComponentName)
    } catch (error) {
      window.NREUM.noticeError(
        new UseDynamicShowcasesError('Failed to extract dynamic showcases'),
        {
          customAttributes: {
            component: showcasesComponentName,
            error: error instanceof Error ? error.message : String(error),
          },
          customError: true,
        },
      )

      return []
    }
  }, [dynamicContentForHome.data, showcasesComponentName])

  const queriesParams = useMemo(
    () =>
      dynamicShowcases.map(
        (dynamicShowCase): UseProductsQueriesParams => ({
          pageSize: maxItemsPerShowcase,
          products: dynamicShowCase.options.skus,
        }),
      ),
    [dynamicShowcases, maxItemsPerShowcase],
  )

  const getProductsQueriesResults = useProductsQueries(queriesParams)

  return useMemo(
    () =>
      dynamicShowcases.map((showcase, idx) => {
        const getProductsQueryResult = getProductsQueriesResults[idx]

        const productsForThisShowcase = getProductsQueryResult?.data?.products ?? []

        const isLoading = dynamicContentForHome.isLoading || getProductsQueryResult.isLoading
        const isError = dynamicContentForHome.isError || getProductsQueryResult.isError
        const error = dynamicContentForHome.error || getProductsQueryResult.error || null

        if (isError && error) {
          window.NREUM.noticeError(
            new UseDynamicShowcasesError('Error loading products for showcase'),
            {
              customAttributes: {
                component: showcasesComponentName,
                showcaseIndex: idx,
                error: error instanceof Error ? error.message : String(error),
              },
              customError: true,
            },
          )
        }

        return {
          ...getProductsQueryResult,
          isLoading,
          isError,
          error,
          data: {
            ...showcase,
            products: productsForThisShowcase,
          },
        } as UseQueryResult<ProductEnhancedDynamicShowcase>
      }),
    [
      dynamicContentForHome.error,
      dynamicContentForHome.isError,
      dynamicContentForHome.isLoading,
      dynamicShowcases,
      getProductsQueriesResults,
      showcasesComponentName,
    ],
  )
}
